<template>
  <section class="group-section d-inline-block">
    <div
      class="text-medium-1 bold group-name pointer d-inline-flex align-items-center w-100"
      @click="expanded = !expanded"
      v-if="isGrouped"
    >
      <div class="d-inline-flex align-items-center group-name-contents">
        <i class="gripper-icon section-gripper" v-if="groupInfo.isSection"></i>
        <collapse-caret class="flex-shrink-0 collapse-caret" :size="23" :expanded="expanded" />
        <i class="ml-2" :class="groupInfo.icon" v-if="groupInfo.icon" />
        <span class="ml-2" v-if="!isEditingGroup">{{ groupInfo.formattedGroupName }}</span>
        <span v-if="group.length && !isEditingGroup">&nbsp;({{ group.length }})</span>
        <span
          :id="'initiative-edit-'"
          class="edit-icon pointer ml-2"
          @click.stop="isEditingGroup = true"
          v-if="groupInfo.isSection && !isEditingGroup"
          ><i></i
        ></span>
        <delete-section-icon
          @click.native.stop
          :section-id="groupInfo.name"
          :tasks-count="group.length"
          class="delete-icon pointer ml-1"
          v-if="groupInfo.isSection && !isEditingGroup"
          @deleted="$emit('delete-section', groupInfo.name)"
        />
        <form class="inline-edit flex-grow-1" @submit.prevent="onTitleSubmit">
          <input
            @click.stop
            @blur="onTitleSubmit"
            @keydown.esc="dismissEdit"
            v-model="groupTitle"
            class="pl-2"
            v-if="isEditingGroup"
            type="text"
            placeholder="Enter section name ([enter] to save)"
            v-autofocus
          />
        </form>
      </div>
    </div>
    <b-collapse class="initiatives-table" v-model="expanded">
      <initiatives-header
        @sort="$emit('sort', $event)"
        @reset="$emit('reset', $event)"
        @filter="$emit('filter', $event)"
        @enable-cfs="onEnableCFs"
        :sorted-by="sortMeta.sortBy"
        :sorted-direction="sortMeta.sortDirection"
        :fields-to-display="fieldsToDisplay"
        :auto-save-column-settings="autoSaveColumnSettings"
        :show-field-settings-in-column="true"
        :team-id="teamId"
        :org-id="orgId"
        :project-id="projectId"
        :show-filter-options="false"
        :show-sort-options="isSortable"
      />
      <div
        class="list-container d-inline-block"
        ref="initiatives"
        :data-id="group.id"
        :data-group-index="groupIndex"
        :class="{ 'mb-2': group.length > 0 }"
      >
        <template v-for="initiative in group">
          <initiative-row
            :data-id="initiative.id"
            :key="initiative.id + ' ' + initiative.keyResultId"
            :orgId="orgId"
            :krId="initiative.keyResultId"
            :goalId="initiative.goalId"
            :initiative="initiative"
            :initiatives="group"
            :fields-to-display="fieldsToDisplay"
            :show-parent="showParent"
            :draggable="!hideGripper && !sortMeta.sortBy"
            gripper-icon-class="drag-handle"
            @save-initiative="$emit('save', { after: $event, before: initiative, groupIndex })"
            @delete-initiatives="$emit('remove', { initiative, groupIndex })"
            @update-initiative="$emit('update', { initiative: $event, groupIndex })"
          />
        </template>
        <add-initiative-placeholder
          :fields-to-display="fieldsToDisplay"
          @click="$emit('add', groupIndex)"
          v-if="!hasNewInitiative(group)"
        />
      </div>
    </b-collapse>
  </section>
</template>

<script>
import CollapseCaret from '@/components/CollapseCaret'
import AddInitiativePlaceholder from '@/components/initiative/AddInitiativePlaceholder.vue'
import InitiativeRow from '@/components/initiative/InitiativeRow'
import InitiativesHeader from '@/components/initiative/InitiativesHeader.vue'
import DeleteSectionIcon from '@/components/project/DeleteSectionIcon.vue'
import ExpandCollapseMixin from '@/mixins/ExpandCollapseMixin'
import isLocalId from '@/utils/initiative/isLocalId'
import showAPIError from '@/utils/showAPIError'
import { BCollapse } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component shows a group of initiatives. Gives capability to add an initiative to that group
 *
 * @emits
 * - sort
 - reset
 - filter
 - save
 - remove
 - update
 */
export default {
  name: 'InitiativeListGroup',
  props: [
    'group',
    'sortMeta',
    'groupInfo',
    'groupIndex',
    'orgId',
    'teamId',
    'projectId',
    'isSortable',
    'fieldsToDisplay',
    'hideGripper',
    'showParent',
    'isExpanded',
    'autoSaveColumnSettings'
  ],
  // orgId, teamId is being used by InitiativeDisplayFieldsMixin
  mixins: [ExpandCollapseMixin],
  components: {
    DeleteSectionIcon,
    AddInitiativePlaceholder,
    InitiativeRow,
    InitiativesHeader,
    BCollapse,
    CollapseCaret
  },
  data() {
    const isGrouped = this.groupInfo?.formattedGroupName
    return {
      expanded: !isGrouped || this.group.length > 0 || this.isExpanded,
      isEditingGroup: false,
      groupTitle: this.groupInfo.formattedGroupName
    }
  },
  computed: {
    isGrouped() {
      return !!this.groupInfo?.formattedGroupName
    }
  },
  methods: {
    ...mapActions('project', ['updateSection']),
    onEnableCFs(cfParams) {
      this.$emit('add-column', R.mapObjIndexed(R.join(','), cfParams))
    },
    hasNewInitiative(group) {
      return group.some(initiative => isLocalId(initiative.id))
    },
    async onTitleSubmit() {
      try {
        await this.updateSection({
          orgId: this.orgId,
          sectionId: this.groupInfo.name,
          payload: {
            title: this.groupTitle
          }
        })
        this.isEditingGroup = false
      } catch (e) {
        showAPIError(e, ['detail', 'title'])
      }
    },
    dismissEdit() {
      this.isEditingGroup = false
      this.groupTitle = this.groupInfo.formattedGroupName
    }
  },
  watch: {
    expanded() {
      this.updateExpansion(this.expanded, '.initiative-row')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_input.scss';

$leftSpacing: 30px;
.group-name {
  padding-left: $leftSpacing;
  margin-bottom: 10px;
}

.group-section {
  margin-bottom: 20px;
  min-width: 100%;
}

.list-container {
  padding-bottom: 6px;
  min-width: 100%;
}

.inline-edit input {
  padding: 4px 8px;
  width: 270px;
}

.group-name:hover {
  .edit-icon,
  .delete-icon {
    display: inline-flex;
  }
}

.edit-icon,
.delete-icon {
  display: none;
}

.gripper-icon {
  position: absolute;
  left: -24px;
  cursor: move;
  opacity: 0;
}
.group-name:hover .gripper-icon {
  opacity: 0.2;
  &:hover {
    opacity: 1;
  }
}

.group-name-contents {
  @include sticky(false, $leftSpacing);
}

.initiatives-table {
  display: inline-block;
  min-width: 100%;
}

@include media-breakpoint-down(md) {
  .group-name {
    padding-left: 0;
  }
  .gripper-icon {
    display: none;
  }
}
</style>
